/**
 * Copyright 2023 Design Barn Inc.
 */

/* eslint-disable @typescript-eslint/naming-convention */

import type { ReadonlyURLSearchParams } from 'next/navigation';

import type { IframeMessagingConfig } from '../contexts/iframe-messaging-context';

import { encode } from './callback-state';
import serializeQueryParams from './serialize-query-params';

const clientId = process.env['NEXT_PUBLIC_GOOGLE_CLIENT_ID'] || '';
const googleRedirectUrl = process.env['NEXT_PUBLIC_GOOGLE_REDIRECT_URL'] || '';

const fbRedirectUrl = process.env['NEXT_PUBLIC_FACEBOOK_REDIRECT_URL'] || '';

const gitHubClientId = process.env['NEXT_PUBLIC_GITHUB_CLIENT_ID'] || '';
const gitHubRedirectUrl = process.env['NEXT_PUBLIC_GITHUB_REDIRECT_URL'] || '';

const dribbbleClientId = process.env['NEXT_PUBLIC_DRIBBBLE_CLIENT_ID'] || '';
const dribbbleRedirectUrl = process.env['NEXT_PUBLIC_DRIBBBLE_REDIRECT_URL'] || '';

export const getUtmParams = (params: ReadonlyURLSearchParams | null): Record<string, string> => {
  const utmParams: Record<string, string> = {};

  if (params) {
    params.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        utmParams[key] = value;
      }
    });
  }

  return utmParams;
};

type Theme = 'lottiefiles';

export const createGoogleOAuthLoginUrl = ({
  config,
  locale,
  metadata,
  returnAction,
  returnOnError,
  returnTo,
  theme,
  utmParams,
}: {
  config: IframeMessagingConfig;
  locale: string;
  metadata: string | undefined;
  returnAction: 'login' | 'register';
  returnOnError?: string;
  returnTo: string;
  theme: Theme;
  utmParams: Record<string, string>;
}): string => {
  const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';

  const state = {
    mode: 'login',
    return_to: returnTo,
    return_on_error: returnOnError,
    return_action: returnAction,
    config,
    utm_params: utmParams,
    theme,
    metadata,
    locale,
  };

  const encodedState = encode(state);

  const params = {
    client_id: clientId,
    redirect_uri: googleRedirectUrl,
    response_type: 'token id_token',
    scope: 'openid email profile',
    state: encodedState,
    nonce: '12345',
    include_granted_scopes: 'true',
  };

  const paramsString = serializeQueryParams(params);

  // Create the Google OAuth authorization URL
  const googleAuthUrl = `${oauth2Endpoint}?${paramsString}`;

  return googleAuthUrl;
};

export const createFbLoginCallbackUrl = ({
  accessToken,
  config,
  locale,
  metadata,
  returnAction,
  returnOnError,
  returnTo,
  theme,
  utmParams,
}: {
  accessToken: string;
  config: IframeMessagingConfig;
  locale: string;
  metadata: string | undefined;
  returnAction: 'login' | 'register';
  returnOnError?: string;
  returnTo: string;
  theme: Theme;
  utmParams: Record<string, string>;
}): string => {
  const state = {
    mode: 'login',
    return_to: returnTo,
    return_action: returnAction,
    return_on_error: returnOnError,
    config,
    theme,
    utm_params: utmParams,
    metadata,
    locale,
  };

  const encodedState = encode(state);

  const params = {
    accessToken,
    state: encodedState,
  };

  const paramsString = serializeQueryParams(params);

  const fbCallbackRedirectUrl = `${fbRedirectUrl}?${paramsString}`;

  return fbCallbackRedirectUrl;
};

export const createGitHubOAuthLoginUrl = ({
  config,
  locale,
  metadata,
  returnAction,
  returnOnError,
  returnTo,
  theme,
  utmParams,
}: {
  config: IframeMessagingConfig;
  locale: string;
  metadata: string | undefined;
  returnAction: 'login' | 'register';
  returnOnError?: string;
  returnTo: string;
  theme: Theme;
  utmParams: Record<string, string>;
}): string => {
  const state = {
    mode: 'login',
    return_to: returnTo,
    return_on_error: returnOnError,
    return_action: returnAction,
    config,
    theme,
    utm_params: utmParams,
    metadata,
    locale,
  };

  const encodedState = encode(state);

  // don't run url encode on the scope string otherwise it won't work because of the ':' getting encoded
  const gitHubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${gitHubClientId}&redirect_uri=${gitHubRedirectUrl}&scope=user:email&state=${encodedState}`;

  return gitHubAuthUrl;
};

export const createDribbbleOAuthLoginUrl = ({
  config,
  locale,
  metadata,
  returnAction,
  returnOnError,
  returnTo,
  theme,
  utmParams,
}: {
  config: IframeMessagingConfig;
  locale: string;
  metadata: string | undefined;
  returnAction: 'login' | 'register';
  returnOnError?: string;
  returnTo: string;
  theme: Theme;
  utmParams: Record<string, string>;
}): string => {
  const state = {
    mode: 'login',
    return_to: returnTo,
    return_on_error: returnOnError,
    return_action: returnAction,
    config,
    theme,
    utm_params: utmParams,
    metadata,
    locale,
  };

  const encodedState = encode(state);

  // don't run url encode on the scope string otherwise it won't work because of the ':' getting encoded
  const gitHubAuthUrl = `https://dribbble.com/oauth/authorize?client_id=${dribbbleClientId}&redirect_uri=${dribbbleRedirectUrl}&scope=public&state=${encodedState}`;

  return gitHubAuthUrl;
};

/*
 * Creates the encoded callback state for the google onetap login
 * flow because it is not possible to pass a state parameter through the process
 * so we need to manually append the state to the callback url.
 */
export const createCallbackEncodedState = ({
  config,
  locale,
  returnAction,
  returnOnError,
  returnTo,
}: {
  config: IframeMessagingConfig;
  locale: string;
  returnAction: 'login' | 'register';
  returnOnError?: string;
  returnTo: string;
}): string => {
  const state = {
    mode: 'login',
    return_to: returnTo,
    return_on_error: returnOnError,
    return_action: returnAction,
    config,
    locale,
  };

  const encodedState = encode(state);

  return encodedState;
};

export const createEnterpriseLoginState = ({
  config,
  locale,
  metadata,
  returnOnError,
  returnTo,
}: {
  config: IframeMessagingConfig;
  locale: string;
  metadata: string | undefined;
  returnOnError?: string;
  returnTo: string;
}): string => {
  const state = {
    mode: 'login',
    return_to: returnTo,
    return_on_error: returnOnError,
    return_action: 'login',
    config,
    metadata,
    locale,
  };

  const encodedState = encode(state);

  return encodedState;
};
